import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    ApiIcon,
    colors,
    LanguageIcon,
    LockOutlineIcon,
    LogoutIcon,
    MonitorCursorIcon,
    TimeIcon
} from "@dotnet/telematics-design-system";

import CompanyLevelTag from "components/CompanyLevel/shared/components/CompanyLevelTag";
import ListChoice from "components/DesignComponents/ListChoice";
import Separator from "components/DesignComponents/Separator";
import AuthContext from "context/auth/authContext";
import { DEV } from "shared/constants/strings";
import { body3RegularStyles, body4Regular } from "shared/styles/typography";
import { COMPANY_LEVELS } from "shared/types/enums";

import { DIALOG_NAMES } from "../types";
import { CONTENT_NAMES, InitialContentProps } from "./types";

const InitialContent = ({ setContent, openDialog }: InitialContentProps) => {
    const {
        beigeDefault,
        blue100,
        blue700,
        gray200,
        gray800,
        gray900,
        textPlaceholder,
        white
    } = colors;

    const { isSuperUser, user, logoutUser } = useContext(AuthContext);

    const { t } = useTranslation();

    const companyLevel = user?.company_level as COMPANY_LEVELS;
    const canManageSecuritySettings = Boolean(user?.password_auth_enabled);

    const getAvatarColors = () => {
        switch (companyLevel) {
            case COMPANY_LEVELS.Basic:
                return { background: gray200, color: gray900 };
            case COMPANY_LEVELS.Standard:
                return { background: beigeDefault, color: gray900 };
            case COMPANY_LEVELS.Premium:
                return { background: gray800, color: white };
            default:
                return { background: blue100, color: blue700 };
        }
    };

    const avatarBgColor = getAvatarColors().background;
    const avatarTextColor = getAvatarColors().color;

    return (
        <>
            <li
                css={css({
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 16px"
                })}
            >
                <div
                    css={css({
                        minWidth: "24px",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: avatarBgColor,
                        borderRadius: "100px",
                        marginRight: "8px",
                        color: avatarTextColor,
                        fontSize: "14px",
                        fontWeight: "600",
                        lineHeight: "20px",
                        letterSpacing: "0.1px"
                    })}
                >
                    {user?.email.charAt(0).toUpperCase()}
                </div>

                <div
                    css={css({
                        "& > div": {
                            ...body3RegularStyles,
                            wordBreak: "break-all"
                        }
                    })}
                >
                    <div
                        css={css({
                            fontWeight: "500",
                            marginBottom: "4px"
                        })}
                    >
                        {user?.email}
                    </div>

                    <div>
                        <span
                            css={css({
                                display: "inline-block",
                                lineHeight: "16px",
                                marginRight: "4px"
                            })}
                        >
                            {t("General##company level")}:
                        </span>

                        <CompanyLevelTag
                            level={companyLevel}
                            onClick={() =>
                                openDialog(DIALOG_NAMES.CompanyLevel)
                            }
                        />
                    </div>
                </div>
            </li>

            <Separator isListSeparator />

            <ListChoice
                title={t("Button##language")}
                iconLeft={<LanguageIcon />}
                onClick={() => setContent(CONTENT_NAMES.Language)}
            />

            <ListChoice
                title={t("Api tokens##tokens")}
                iconLeft={<ApiIcon />}
                onClick={() => openDialog(DIALOG_NAMES.ApiTokens)}
            />

            {canManageSecuritySettings && (
                <ListChoice
                    title={t("Security##security and account")}
                    iconLeft={<LockOutlineIcon />}
                    onClick={() => openDialog(DIALOG_NAMES.SecurityAndAccount)}
                />
            )}

            <ListChoice
                title={t("General##time zone")}
                iconLeft={<TimeIcon />}
                onClick={() => setContent(CONTENT_NAMES.TimeZone)}
            />

            <ListChoice
                title={t("General##tutorials")}
                iconLeft={<MonitorCursorIcon />}
                onClick={() => setContent(CONTENT_NAMES.TutorialMode)}
            />

            <Separator isListSeparator />

            {isSuperUser && (
                <>
                    <ListChoice
                        title={t("General##banner management")}
                        onClick={() =>
                            openDialog(DIALOG_NAMES.BannerManagement)
                        }
                    />

                    <Separator isListSeparator />
                </>
            )}

            <ListChoice
                title={t("Button##logout")}
                iconLeft={<LogoutIcon />}
                onClick={() => logoutUser()}
            />

            <Separator isListSeparator />

            <li
                css={[
                    body4Regular,
                    {
                        color: textPlaceholder,
                        padding: "12px 16px",
                        textAlign: "right",
                        wordBreak: "break-word"
                    }
                ]}
            >
                {t("Settings##version")}:{" "}
                {import.meta.env.VITE_APP_VERSION ?? DEV}
            </li>
        </>
    );
};

export default InitialContent;
